import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Typography,
    Box, IconButton, Grid2, CircularProgress, Alert,
    Tooltip, Button, Chip, Divider, Paper, Stack,
    Accordion, AccordionSummary, AccordionDetails,
    TextField, InputAdornment
} from '@mui/material';
import {
    Close as CloseIcon,
    AccountBalance as WalletIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    ViewModule as GridViewIcon,
    ViewList as ListViewIcon,
    FilterList as FilterIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import BalanceCard from './BalanceCard';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useWallet } from '../context/WagmiWalletContext';
import { useContracts } from '../context/ContractContext';
import logger from '../../utils/logger';

// Import MEMER components
import { MemerH4, MemerBody } from '../ui/memer/MemerTypography';

interface ContractsBalancesDialogProps {
    open: boolean;
    onClose: () => void;
}

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'multisig' | 'contracts' | 'hasBalance';

const ContractsBalancesDialog: React.FC<ContractsBalancesDialogProps> = ({ open, onClose }) => {
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState<FilterType>('all');
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
        multisig: true,
        contracts: true,
        summary: false
    });

    const { provider } = useWallet();
    const { contracts, deploymentDetails, memerCoreAddress } = useContracts();

    // Use the dashboard data hook
    const {
        multiSigBalances,
        contractBalances,
        isLoading,
        error,
        lastUpdated,
        refreshData
    } = useDashboardData({
        provider,
        deploymentDetails: deploymentDetails || {},
        contracts,
        memerCoreAddress
    });

    // Trigger data refresh when dialog opens
    useEffect(() => {
        if (open) {
            refreshData().catch(err => {
                logger.error("Error refreshing dashboard data:", err);
            });
        }
    }, [open, refreshData]);

    // Filter and search logic
    const filterData = (data: any[], type: 'multisig' | 'contract') => {
        return data.filter(item => {
            // Search filter
            const matchesSearch = !searchQuery ||
                item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.address.toLowerCase().includes(searchQuery.toLowerCase());

            // Type filter
            const matchesFilter = filter === 'all' ||
                (filter === 'multisig' && type === 'multisig') ||
                (filter === 'contracts' && type === 'contract') ||
                (filter === 'hasBalance' && parseFloat(item.balance) > 0);

            return matchesSearch && matchesFilter;
        });
    };

    const filteredMultiSig = filterData(multiSigBalances, 'multisig');
    const filteredContracts = filterData(contractBalances, 'contract');

    // Calculate summary statistics
    const totalETH = [...multiSigBalances, ...contractBalances]
        .reduce((sum, item) => sum + parseFloat(item.balance || '0'), 0);

    const totalContracts = multiSigBalances.length + contractBalances.length;
    const contractsWithBalance = [...multiSigBalances, ...contractBalances]
        .filter(item => parseFloat(item.balance || '0') > 0).length;

    const handleSectionToggle = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const renderBalanceCards = (data: any[], type: 'multisig' | 'contract') => {
        if (data.length === 0) {
            return (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                        {searchQuery || filter !== 'all'
                            ? `No ${type === 'multisig' ? 'MultiSig wallets' : 'contracts'} match your filters`
                            : `No ${type === 'multisig' ? 'MultiSig wallets' : 'contracts'} found`
                        }
                    </Typography>
                </Paper>
            );
        }

        if (viewMode === 'list') {
            return (
                <Stack spacing={1}>
                    {data.map((item, index) => (
                        <BalanceCard
                            key={`${type}-${index}-${item.address}`}
                            {...item}
                            variant="compact"
                        />
                    ))}
                </Stack>
            );
        }

        return (
            <Grid2 container spacing={2}>
                {data.map((item, index) => (
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={`${type}-${index}-${item.address}`}>
                        <BalanceCard
                            {...item}
                            variant="default"
                        />
                    </Grid2>
                ))}
            </Grid2>
        );
    };

    return (
        <Dialog
            fullWidth
            maxWidth="xl"
            open={open}
            onClose={onClose}
            aria-labelledby="contracts-balances-dialog-title"
            aria-describedby="contracts-balances-dialog-description"
            disableAutoFocus={false}
            disableEnforceFocus={false}
            disableRestoreFocus={false}
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: '900px'
                }
            }}
        >
            <DialogTitle id="contracts-balances-dialog-title">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <WalletIcon sx={{ color: '#D4AF37', fontSize: 28 }} />
                        <Box>
                            <MemerH4 sx={{ mb: 0, color: '#D4AF37' }}>
                                Contracts & Balances
                            </MemerH4>
                            <Typography variant="caption" color="text.secondary">
                                Monitor all your MultiSig wallets and smart contracts
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Refresh Data">
                            <span>
                                <IconButton
                                    onClick={() => refreshData()}
                                    size="small"
                                    disabled={isLoading}
                                    sx={{
                                        animation: isLoading ? 'spin 1s linear infinite' : 'none',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' }
                                        }
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={viewMode === 'grid' ? "Switch to List View" : "Switch to Grid View"}>
                            <IconButton
                                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                                size="small"
                            >
                                {viewMode === 'grid' ? <ListViewIcon /> : <GridViewIcon />}
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <div id="contracts-balances-dialog-description">
                    {/* Summary Statistics */}
                    <Accordion
                        expanded={expandedSections.summary}
                        onChange={() => handleSectionToggle('summary')}
                        sx={{ boxShadow: 'none', borderBottom: '1px solid rgba(0,0,0,0.12)' }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon color="primary" />
                                <Typography variant="h6">Portfolio Summary</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                                        <Typography variant="h4" color="primary.main" fontWeight="bold">
                                            {totalETH.toFixed(4)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total ETH Balance
                                        </Typography>
                                    </Paper>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                                        <Typography variant="h4" color="success.main" fontWeight="bold">
                                            {totalContracts}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Contracts
                                        </Typography>
                                    </Paper>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 4 }}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                                            {contractsWithBalance}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            With Balance
                                        </Typography>
                                    </Paper>
                                </Grid2>
                            </Grid2>
                        </AccordionDetails>
                    </Accordion>

                    {/* Search and Filter Controls */}
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                        <Grid2 container spacing={2} alignItems="center">
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search contracts by name or address..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label="All"
                                        onClick={() => setFilter('all')}
                                        color={filter === 'all' ? 'primary' : 'default'}
                                        variant={filter === 'all' ? 'filled' : 'outlined'}
                                        size="small"
                                    />
                                    <Chip
                                        label="MultiSig"
                                        onClick={() => setFilter('multisig')}
                                        color={filter === 'multisig' ? 'primary' : 'default'}
                                        variant={filter === 'multisig' ? 'filled' : 'outlined'}
                                        size="small"
                                    />
                                    <Chip
                                        label="Contracts"
                                        onClick={() => setFilter('contracts')}
                                        color={filter === 'contracts' ? 'primary' : 'default'}
                                        variant={filter === 'contracts' ? 'filled' : 'outlined'}
                                        size="small"
                                    />
                                    <Chip
                                        label="Has Balance"
                                        onClick={() => setFilter('hasBalance')}
                                        color={filter === 'hasBalance' ? 'primary' : 'default'}
                                        variant={filter === 'hasBalance' ? 'filled' : 'outlined'}
                                        size="small"
                                    />
                                </Box>
                            </Grid2>
                        </Grid2>
                    </Box>

                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            Error loading data: {error}
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => refreshData()}
                                sx={{ mt: 1, ml: 1 }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {/* Loading State */}
                    {isLoading && multiSigBalances.length === 0 && contractBalances.length === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Loading contract data...</Typography>
                        </Box>
                    )}

                    {/* Main Content */}
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        {/* MultiSig Wallets Section */}
                        <Accordion
                            expanded={expandedSections.multisig}
                            onChange={() => handleSectionToggle('multisig')}
                            sx={{ boxShadow: 'none' }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WalletIcon color="primary" />
                                    <Typography variant="h6">
                                        MultiSig Wallets ({filteredMultiSig.length})
                                    </Typography>
                                    {isLoading && (
                                        <CircularProgress size={16} />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderBalanceCards(filteredMultiSig, 'multisig')}
                            </AccordionDetails>
                        </Accordion>

                        <Divider />

                        {/* Smart Contracts Section */}
                        <Accordion
                            expanded={expandedSections.contracts}
                            onChange={() => handleSectionToggle('contracts')}
                            sx={{ boxShadow: 'none' }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FilterIcon color="secondary" />
                                    <Typography variant="h6">
                                        Smart Contracts ({filteredContracts.length})
                                    </Typography>
                                    {isLoading && (
                                        <CircularProgress size={16} />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderBalanceCards(filteredContracts, 'contract')}
                            </AccordionDetails>
                        </Accordion>
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        p: 2,
                        borderTop: '1px solid rgba(0,0,0,0.12)',
                        bgcolor: 'grey.50',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            {isLoading ? "Refreshing data..." : `Showing ${filteredMultiSig.length + filteredContracts.length} of ${totalContracts} contracts`}
                        </Typography>
                        {lastUpdated && (
                            <Typography variant="caption" color="text.secondary">
                                Last Updated: {new Date(lastUpdated).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ContractsBalancesDialog;
